import { PageGen, PdfBrush, PdfPen, PdfPoint, PdfRect, PdfSize, PdfUnit } from '../pdf';
import { GUtil } from '../gutil';
import { C } from '../../core/c';

/**
 * GeoBoard - A grid board for geometric exercises
 * Used for transformation exercises like rotation, symmetry, translation
 */
export class GeoBoard {
  public readonly width: number;
  public readonly height: number;
  public readonly distance: number;
  private readonly holeSize: number;

  public readonly cutSize: PdfSize | null;
  public readonly size: PdfSize;
  public readonly origin: PdfPoint;
  public readonly originMini: PdfPoint;
  public readonly rect: PdfRect;
  public readonly rectMini: PdfRect;
  public readonly rectBelow: PdfRect;
  public readonly distanceMini: number;

  /**
   * Creates a 9x9 board with 20mm spacing (920 variant)
   */
  public static Board920(): GeoBoard {
    return new GeoBoard(9, 9, PdfUnit.FromMillimeters(20), PdfUnit.FromMillimeters(3));
  }

  /**
   * Creates an 8x8 board with specific spacing (8 variant)
   */
  public static Board8(): GeoBoard {
    return new GeoBoard(
      8,
      8,
      PdfUnit.FromMillimeters(121.5) / 7,
      PdfUnit.FromMillimeters(3),
      new PdfSize(PdfUnit.FromMillimeters(145), PdfUnit.FromMillimeters(145))
    );
  }

  constructor(
    width: number,
    height: number,
    distance: number,
    holeSize: number,
    cutSize: PdfSize | null = null
  ) {
    this.width = width;
    this.height = height;
    this.distance = distance;
    this.holeSize = holeSize;
    this.cutSize = cutSize;
    this.size = new PdfSize(PdfSize.A4.width, PdfSize.A4.width);

    const originP = (PdfSize.A4.width - (width - 1) * this.distance) / 2;
    this.origin = new PdfPoint(originP, originP);
    this.rect = new PdfRect(
      this.origin.x,
      this.origin.y,
      (width - 1) * this.distance,
      (height - 1) * this.distance
    );

    const miniMargin = PdfUnit.FromMillimeters(10);
    this.rectBelow = new PdfRect(
      0,
      this.size.height,
      PdfSize.A4.width,
      PdfSize.A4.height - this.size.height
    );
    this.rectMini = new PdfRect(
      this.rectBelow.x + miniMargin,
      this.rectBelow.y + miniMargin,
      this.rectBelow.width - 2 * miniMargin,
      this.rectBelow.height - 2 * miniMargin
    );

    this.distanceMini = Math.min(
      this.rectMini.width / (width - 1),
      this.rectMini.height / (height - 1)
    );
    this.originMini = new PdfPoint(
      this.rectMini.x + (this.rectMini.width - (width - 1) * this.distanceMini) / 2,
      this.rectMini.y + (this.rectMini.height - (height - 1) * this.distanceMini) / 2
    );
  }

  /**
   * Get point coordinates on the main board
   */
  public point(x: number, y: number): PdfPoint {
    return new PdfPoint(this.origin.x + x * this.distance, this.origin.y + y * this.distance);
  }

  /**
   * Get point coordinates from C object
   */
  public pointFromC(c: C): PdfPoint {
    return this.point(c.x, c.y);
  }

  /**
   * Get point coordinates on the mini board
   */
  public pointMini(x: number, y: number): PdfPoint {
    return new PdfPoint(
      this.originMini.x + x * this.distanceMini,
      this.originMini.y + y * this.distanceMini
    );
  }

  /**
   * Get mini point coordinates from C object
   */
  public pointMiniFromC(c: C): PdfPoint {
    return this.pointMini(c.x, c.y);
  }

  /**
   * Draw the main GeoBoard grid
   */
  public draw(g: PageGen): void {
    for (let x = 0; x < this.width; x++) {
      for (let y = 0; y < this.height; y++) {
        const p = this.point(x, y);
        const e = new PdfRect(
          p.x - this.holeSize / 2,
          p.y - this.holeSize / 2,
          this.holeSize,
          this.holeSize
        );
        g.drawEllipse(GUtil.cutPen, null, e);
      }
    }

    g.drawLine(
      GUtil.cutPen,
      new PdfPoint(0, PdfSize.A4.width),
      new PdfPoint(PdfSize.A4.width, PdfSize.A4.width)
    );

    if (this.cutSize) {
      const rect = new PdfRect(
        (PdfSize.A4.width - this.cutSize.width) / 2,
        (PdfSize.A4.width - this.cutSize.height) / 2,
        this.cutSize.width,
        this.cutSize.height
      );
      g.drawRectangle(GUtil.cutPen, null, rect);
    }
  }

  /**
   * Draw the mini GeoBoard grid
   */
  public drawMini(g: PageGen): void {
    const holeSize = PdfUnit.FromMillimeters(1.5);
    for (let x = 0; x < this.width; x++) {
      for (let y = 0; y < this.height; y++) {
        const p = this.pointMini(x, y);
        const e = new PdfRect(p.x - holeSize / 2, p.y - holeSize / 2, holeSize, holeSize);
        g.drawEllipse(GUtil.cutPen, null, e);
      }
    }

    g.drawLine(
      GUtil.cutPen,
      new PdfPoint(0, PdfSize.A4.width),
      new PdfPoint(PdfSize.A4.width, PdfSize.A4.width)
    );
  }

  /**
   * Draw a point at the specified grid position
   */
  public drawPoint(
    g: PageGen,
    x: number,
    y: number,
    pen: PdfPen | null = null,
    brush: PdfBrush | null = null,
    size: number | null = null
  ): void {
    const sz = size ?? this.holeSize;
    const p = this.point(x, y);
    const e = new PdfRect(p.x - sz / 2, p.y - sz / 2, sz, sz);

    if (brush) g.drawEllipse(null, brush, e);
    if (pen) g.drawEllipse(pen, null, e);
  }

  /**
   * Draw a point at the specified C coordinate
   */
  public drawPointFromC(
    g: PageGen,
    point: C,
    pen: PdfPen | null = null,
    brush: PdfBrush | null = null,
    size: number | null = null
  ): void {
    this.drawPoint(g, point.x, point.y, pen, brush, size);
  }
}

/**
 * GeoBoard shape types for transformations
 */
export enum EGeoBoardShapeType {
  Square,
  Rect,
  Triangle,
  TetrisT,
  TetrisJ,
  TetrisL,
  TetrisZ,
  TetrisS,
}

/**
 * GeoBoard shape definition
 */
export interface GeoBoardShape {
  type: EGeoBoardShapeType;
  points: C[];
  size: C;
  origin: C;
  surface: number;
}

/**
 * Generate random GeoBoard shapes
 */
export function randomGeoBoardShape(
  shapes: Map<EGeoBoardShapeType, C[]>,
  boardWidth: number,
  boardHeight: number,
  random: () => number
): GeoBoardShape {
  const types = Array.from(shapes.keys());
  const type = types[Math.floor(random() * types.length)];
  const sizeOptions = shapes.get(type)!;
  const sizeOption = sizeOptions[Math.floor(random() * sizeOptions.length)];

  const points = generateShapePoints(type, sizeOption, boardWidth, boardHeight, random);

  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y));
  const maxY = Math.max(...points.map((p) => p.y));

  const size = new C(maxX - minX, maxY - minY);

  let surface: number;
  switch (type) {
    case EGeoBoardShapeType.Square:
    case EGeoBoardShapeType.Rect:
      surface = sizeOption.x * sizeOption.y;
      break;
    case EGeoBoardShapeType.Triangle:
      surface = (sizeOption.x * sizeOption.y) / 2;
      break;
    default:
      surface = size.x * size.y;
      break;
  }

  return {
    type,
    points,
    size,
    origin: new C(minX, minY),
    surface,
  };
}

function generateShapePoints(
  type: EGeoBoardShapeType,
  size: C,
  boardWidth: number,
  boardHeight: number,
  random: () => number
): C[] {
  const maxX = boardWidth - size.x - 1;
  const maxY = boardHeight - size.y - 1;
  const startX = Math.floor(random() * Math.max(1, maxX));
  const startY = Math.floor(random() * Math.max(1, maxY));

  switch (type) {
    case EGeoBoardShapeType.Square:
    case EGeoBoardShapeType.Rect:
      return [
        new C(startX, startY),
        new C(startX + size.x, startY),
        new C(startX + size.x, startY + size.y),
        new C(startX, startY + size.y),
      ];
    case EGeoBoardShapeType.Triangle:
      return [
        new C(startX, startY + size.y),
        new C(startX + size.x, startY + size.y),
        new C(startX + size.x / 2, startY),
      ];
    case EGeoBoardShapeType.TetrisT:
      return [
        new C(startX, startY),
        new C(startX + size.x, startY),
        new C(startX + size.x, startY + 1),
        new C(startX + Math.floor(size.x / 2) + 1, startY + 1),
        new C(startX + Math.floor(size.x / 2) + 1, startY + size.y),
        new C(startX + Math.floor(size.x / 2), startY + size.y),
        new C(startX + Math.floor(size.x / 2), startY + 1),
        new C(startX, startY + 1),
      ];
    default:
      // Default to rectangle for unsupported shapes
      return [
        new C(startX, startY),
        new C(startX + size.x, startY),
        new C(startX + size.x, startY + size.y),
        new C(startX, startY + size.y),
      ];
  }
}

/**
 * Check if two shapes intersect (bounding box)
 */
export function shapesIntersect(shape1: C[], shape2: C[]): boolean {
  const s1MinX = Math.min(...shape1.map((p) => p.x));
  const s1MaxX = Math.max(...shape1.map((p) => p.x));
  const s1MinY = Math.min(...shape1.map((p) => p.y));
  const s1MaxY = Math.max(...shape1.map((p) => p.y));

  const s2MinX = Math.min(...shape2.map((p) => p.x));
  const s2MaxX = Math.max(...shape2.map((p) => p.x));
  const s2MinY = Math.min(...shape2.map((p) => p.y));
  const s2MaxY = Math.max(...shape2.map((p) => p.y));

  const noIntersection = s1MaxX < s2MinX || s1MinX > s2MaxX || s1MaxY < s2MinY || s1MinY > s2MaxY;
  return !noIntersection;
}

/**
 * Default shape options for GeoBoard transformations
 */
export function getDefaultShapeOptions(): Map<EGeoBoardShapeType, C[]> {
  const options = new Map<EGeoBoardShapeType, C[]>();
  options.set(EGeoBoardShapeType.Square, [new C(2, 2), new C(2, 2)]);
  options.set(EGeoBoardShapeType.Rect, [
    new C(2, 3),
    new C(3, 2),
    new C(2, 3),
    new C(3, 2),
    new C(4, 2),
    new C(2, 4),
    new C(4, 1),
    new C(1, 4),
  ]);
  options.set(EGeoBoardShapeType.Triangle, [
    new C(2, 2),
    new C(2, 3),
    new C(3, 2),
    new C(2, 3),
    new C(3, 2),
    new C(4, 2),
    new C(2, 4),
  ]);
  options.set(EGeoBoardShapeType.TetrisT, [new C(2, 3), new C(3, 2)]);
  options.set(EGeoBoardShapeType.TetrisJ, [new C(2, 3), new C(3, 2)]);
  options.set(EGeoBoardShapeType.TetrisL, [new C(2, 3), new C(3, 2)]);
  options.set(EGeoBoardShapeType.TetrisZ, [new C(2, 3), new C(3, 2)]);
  options.set(EGeoBoardShapeType.TetrisS, [new C(2, 3), new C(3, 2)]);
  return options;
}
